import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "primary"
  | "accent"
  | "accent-strong"
  | "secondary"
  | "ghost"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-[var(--radius-2)] " +
    "font-sans font-medium leading-none whitespace-nowrap " +
    "border transition-colors duration-[--duration-fast] " +
    "disabled:cursor-not-allowed disabled:bg-bg-3 disabled:text-fg-4 disabled:border-border-1",
  {
    variants: {
      variant: {
        // Primary — ink-inverted. White-on-ink in dark, ink-on-paper in light.
        // Hover token (`fg-1-hover`) stays in the same family so the button
        // never collapses into the page background.
        primary:
          "bg-fg-1 text-bg-1 border-fg-1 hover:bg-fg-1-hover hover:border-fg-1-hover",
        // Accent — bright scoped teal. Used for non-button accents (text links,
        // chip text) where the high-chroma teal reads as a signal, not a block.
        // Solid CTAs reach for `accent-strong` instead.
        accent:
          "bg-accent text-accent-fg border-accent hover:bg-accent-hover hover:border-accent-hover",
        // Deeper teal fill — canonical scope-affirming CTA ("Verify", "Run scan",
        // "Re-run scan", "Approve"). On dark, --color-accent-strong is a deeper
        // cyan than --color-accent; on light, the two are the same deep teal —
        // the bright accent only exists in dark mode.
        "accent-strong":
          "bg-accent-strong text-accent-strong-fg border-accent-strong hover:bg-accent-strong-hover hover:border-accent-strong-hover",
        // Secondary — paper outline. Sits next to a primary as the "or do this".
        secondary: "bg-bg-1 text-fg-1 border-border-2 hover:bg-bg-2",
        // Ghost — dismiss, cancel, or third-rank action.
        ghost: "bg-transparent text-fg-1 border-transparent hover:bg-bg-2",
        // Danger — destructive confirmation (delete, revoke). Red filled button.
        danger: "text-bg-1 bg-sev-crit border-sev-crit hover:bg-sev-crit/85",
      },
      // Heights are tuned so a `sm` button lines up with a sibling input
      // at the same density (search field, inline action). `md` matches the
      // default form-input row.
      size: {
        sm: "h-8 px-3 text-[13px]",
        md: "h-10 px-4 text-[14px]",
        lg: "h-11 px-5 text-[15px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export function buttonClasses(
  variant: ButtonVariant = "primary",
  extra = "",
  size: ButtonSize = "md",
): string {
  return cn(buttonVariants({ variant, size }), extra);
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    children?: ReactNode;
  };

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button className={cn(buttonVariants({ variant, size }), className)} {...rest}>
      {children}
    </button>
  );
}
